import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Megaphone, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useUrlState } from '../../hooks/useUrlState';
import useAppStore from '../../store/appStore';
import { onMessagesChange, getLeadDetails, markConversationAsRead, searchLeads, getOrCreateConversation } from '../../firebase/conversation.service';
import { formatInboxTime, truncateMessagePreview } from '../../utils/inboxHelpers';
import MobileChatView from './MobileChatView';
import MobileBroadcastModal from './MobileBroadcastModal';

const MobileInbox = ({ conversations: passedConversations = [], showBroadcast, setShowBroadcast }) => {
    const { user, loading: authLoading } = useAuth();
    const location = useLocation();

    // ── Global store (single source of truth) ───────────────
    const cachedMessagesByConvo = useAppStore((s) => s.messagesByConversation);
    const storeSetMessages = useAppStore((s) => s.setMessages);
    const storeSetActiveConversation = useAppStore((s) => s.setActiveConversation);
    const loadingConversations = useAppStore((s) => s.loadingStates.conversations);

    // ── URL state — active conversation ─────────────────────
    const [urlConversation, setUrlConversation] = useUrlState('conversation', '');

    // Use passed conversations from parent (already from store)
    const conversations = passedConversations;

    // ── Local UI state ──────────────────────────────────────
    const [activeChat, setActiveChat] = useState(urlConversation || null);
    const [activeLeadData, setActiveLeadData] = useState(null);
    const [channel, setChannel] = useState('sms');
    const [messageInput, setMessageInput] = useState('');
    const [messages, setMessages] = useState(
        (urlConversation && cachedMessagesByConvo[urlConversation]) || []
    );
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [loadingLeadDetails, setLoadingLeadDetails] = useState(false);
    const [toastMessage, setToastMessage] = useState(null);
    const [filter, setFilter] = useState('all');
    const [searchInput, setSearchInput] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);

    const msgUnsubRef = useRef(null);

    // If navigated from another page requesting a specific conversation
    useEffect(() => {
        const openId = location?.state?.openConversationId;
        const channelHint = location?.state?.channelHint;
        if (openId) {
            setActiveChat(openId);
            setUrlConversation(openId);
            storeSetActiveConversation(openId);
            if (channelHint === 'sms' || channelHint === 'email') {
                setChannel(channelHint);
            }
            // Clear location state so back-navigation doesn't re-trigger
            window.history.replaceState({}, '');
        }
    }, [location?.state?.openConversationId]); // eslint-disable-line

    // Toast handler for broadcast
    const handleShowToast = (toastData) => {
        setToastMessage(toastData);
        setTimeout(() => setToastMessage(null), 3000);
    };

    // ── Lead search ─────────────────────────────────────────
    const handleSearch = async (value) => {
        setSearchInput(value);
        if (!value.trim()) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }
        if (!user?.uid) return;
        setIsSearching(true);
        setSearchLoading(true);
        setSearchError(null);
        try {
            const results = await searchLeads(user.uid, value);
            setSearchResults(results);
        } catch (err) {
            console.error('Search error:', err);
            setSearchError('Failed to search leads');
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSelectLead = async (lead) => {
        if (!user?.uid) return;
        try {
            setSearchLoading(true);
            const conversation = await getOrCreateConversation(user.uid, lead);
            setSearchInput('');
            setSearchResults([]);
            setIsSearching(false);
            openChat(conversation.id);
        } catch (err) {
            console.error('Error opening conversation:', err);
            setSearchError('Failed to open conversation');
        } finally {
            setSearchLoading(false);
        }
    };

    // ── Messages: real-time listener with per-conversation caching ──
    useEffect(() => {
        if (msgUnsubRef.current) {
            msgUnsubRef.current();
            msgUnsubRef.current = null;
        }

        if (!activeChat) {
            setMessages([]);
            return;
        }

        const cached = cachedMessagesByConvo[activeChat];
        if (cached && cached.length > 0) {
            setMessages(cached);
            setLoadingMessages(false);
        } else {
            setLoadingMessages(true);
        }

        markConversationAsRead(activeChat);

        const unsubscribe = onMessagesChange(activeChat, (messageList) => {
            setMessages(messageList);
            setLoadingMessages(false);
            storeSetMessages(activeChat, messageList);
        });
        msgUnsubRef.current = unsubscribe;

        return () => {
            if (msgUnsubRef.current) {
                msgUnsubRef.current();
                msgUnsubRef.current = null;
            }
        };
    }, [activeChat]); // eslint-disable-line

    // Load lead details when active chat changes
    useEffect(() => {
        if (!activeChat || !user?.uid) {
            setActiveLeadData(null);
            return;
        }

        const activeChatConvo = conversations.find((c) => c.id === activeChat);
        if (!activeChatConvo?.leadId) {
            setActiveLeadData(null);
            return;
        }

        const fetchLeadDetails = async () => {
            setLoadingLeadDetails(true);
            try {
                const leadData = await getLeadDetails(user.uid, activeChatConvo.leadId);
                setActiveLeadData(leadData);
            } catch (error) {
                console.error('Error fetching lead details:', error);
                setActiveLeadData(null);
            } finally {
                setLoadingLeadDetails(false);
            }
        };

        fetchLeadDetails();
    }, [activeChat, user?.uid, conversations]);

    const activeChatData = conversations.find((c) => c.id === activeChat);

    // Merge conversation data with full lead data for details panel
    const chatWithLeadData = activeChatData ? {
        ...activeChatData,
        ...activeLeadData,
        id: activeLeadData?.id || activeChatData?.leadId,
    } : null;

    const handleSendMessage = () => {
        if (!messageInput.trim()) return;
        setMessageInput('');
    };

    const openChat = (id) => {
        setActiveChat(id);
        setUrlConversation(id);
        storeSetActiveConversation(id);
    };

    const closeChat = () => {
        setActiveChat(null);
        setUrlConversation('');
        storeSetActiveConversation(null);
        setActiveLeadData(null);
    };

    if (activeChat) {
        return (
            <MobileChatView
                chat={activeChatData}
                leadData={chatWithLeadData}
                messages={messages}
                channel={channel}
                setChannel={setChannel}
                messageInput={messageInput}
                setMessageInput={setMessageInput}
                handleSendMessage={handleSendMessage}
                onBack={closeChat}
                loading={loadingMessages}
                loadingDetails={loadingLeadDetails}
            />
        );
    }

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-notion-border">
                <h2 className="text-lg font-semibold text-notion-text">Inbox</h2>
                <button
                    className="flex items-center gap-2 px-3 py-1 text-sm font-medium text-white bg-stone-800 rounded hover:bg-stone-700 transition"
                    onClick={() => setShowBroadcast(true)}
                >
                    <Megaphone className="w-4 h-4" /> Broadcast
                </button>
            </div>

            {/* Search and Filters */}
            <div className="p-4 border-b border-notion-border space-y-3">
                <input
                    type="text"
                    placeholder="Search leads..."
                    value={searchInput}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-notion-border rounded focus:outline-none focus:ring-1 focus:ring-stone-800"
                />
                {searchError && <p className="text-xs text-red-500">{searchError}</p>}
                <div className="flex gap-2">
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-3 py-1 text-xs font-medium rounded transition ${filter === 'all' ? 'text-white bg-stone-800 hover:bg-stone-700' : 'text-stone-800 bg-stone-100 hover:bg-stone-200'}`}
                    >All</button>
                    <button
                        onClick={() => setFilter('unread')}
                        className={`px-3 py-1 text-xs font-medium rounded transition ${filter === 'unread' ? 'text-white bg-stone-800 hover:bg-stone-700' : 'text-stone-800 bg-stone-100 hover:bg-stone-200'}`}
                    >Unread</button>
                </div>
            </div>

            {/* Search Results */}
            {isSearching && searchResults.length > 0 && (
                <div className="border-b border-notion-border max-h-64 overflow-y-auto bg-stone-50">
                    <p className="px-4 py-2 text-xs font-medium text-stone-500">SEARCH RESULTS</p>
                    {searchResults.map((lead) => (
                        <button
                            key={lead.id}
                            onClick={() => handleSelectLead(lead)}
                            disabled={searchLoading}
                            className="w-full text-left px-4 py-3 border-b border-notion-border hover:bg-stone-100 transition disabled:opacity-50"
                        >
                            <h3 className="font-medium text-sm text-notion-text">{lead.fullName || lead.name}</h3>
                            <p className="text-xs text-stone-500 truncate">{lead.email || lead.phone}</p>
                        </button>
                    ))}
                </div>
            )}
            {isSearching && searchLoading && (
                <div className="px-4 py-6 text-center">
                    <p className="text-sm text-stone-500">Searching...</p>
                </div>
            )}
            {isSearching && searchResults.length === 0 && !searchLoading && searchInput && (
                <div className="px-4 py-6 text-center">
                    <p className="text-sm text-stone-500">No leads found</p>
                </div>
            )}

            {/* Conversation List */}
            {!isSearching && (
            <div className="flex-1 overflow-y-auto">
                {loadingConversations ? (
                    <div className="p-4 text-center">
                        <p className="text-sm text-stone-500">Loading conversations...</p>
                    </div>
                ) : (() => {
                    const filtered = filter === 'unread'
                        ? conversations.filter(c => (c.unreadCount > 0) || (c.unread === true))
                        : conversations;
                    if (filtered.length === 0) {
                        return (
                            <div className="p-4 text-center">
                                <p className="text-sm text-stone-500">
                                    {filter === 'unread' ? 'No unread conversations' : 'No active conversations. Search for leads to start chatting!'}
                                </p>
                            </div>
                        );
                    }
                    return filtered.map((convo) => {
                        const displayTime = formatInboxTime(convo.lastUpdated || convo.lastMessageAt || convo.time);
                        const messagePreview = truncateMessagePreview(
                            convo.lastMessage || convo.preview || '',
                            45,
                            {
                                hasAttachment: convo.hasAttachment,
                                attachmentType: convo.attachmentType,
                                isSystem: convo.isSystemMessage,
                            }
                        );
                        // Check for unread: support both unreadCount (new) and boolean unread (legacy)
                        const hasUnread = (convo.unreadCount > 0) || (convo.unread === true);
                        return (
                            <div
                                key={convo.id}
                                onClick={() => openChat(convo.id)}
                                className="flex items-center justify-between p-4 border-b border-notion-border cursor-pointer hover:bg-stone-50 transition"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className={`text-notion-text ${hasUnread ? 'font-semibold' : 'font-medium'}`}>{convo.name}</h3>
                                        {hasUnread && convo.unreadCount > 1 && (
                                            <span className="text-[10px] font-medium text-stone-600 bg-stone-100 px-1.5 py-0.5 rounded-full">
                                                {convo.unreadCount}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-notion-text-light truncate">{messagePreview}</p>
                                </div>
                                <div className="flex items-center gap-2 ml-2">
                                    <span className="text-xs text-notion-text-light whitespace-nowrap flex-shrink-0">{displayTime}</span>
                                    {hasUnread && <div className="w-2 h-2 bg-stone-800 rounded-full"></div>}
                                </div>
                            </div>
                        );
                    });
                })()}
            </div>
            )}

            {/* Mobile Broadcast Modal */}
            {showBroadcast && <MobileBroadcastModal onClose={() => setShowBroadcast(false)} onToastShow={handleShowToast} />}

            {/* Toast Notification */}
            {toastMessage && (
                <div className={`fixed bottom-4 left-4 right-4 p-3 rounded-lg text-sm text-center z-50 ${toastMessage.type === 'success' ? 'bg-green-100 text-green-800' :
                    toastMessage.type === 'error' ? 'bg-red-100 text-red-800' :
                        'bg-amber-100 text-amber-800'
                    }`}>
                    {toastMessage.text}
                </div>
            )}
        </div>
    );
};

export default MobileInbox;
